import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import FamilyApi from "../../services/FamilyApi";
import ProductApi from "../../services/ProductApi";

import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

type RequiredErrors = {
  family: boolean;
  status: boolean;
  name: boolean;
  description: boolean;
  partNumber: boolean;
};

const createInitialRequiredErrors = (): RequiredErrors => ({
  family: false,
  status: false,
  name: false,
  description: false,
  partNumber: false,
});

const CreateProductForm: React.FC = () => {
  const { plantId } = useAuth();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [families, setFamilies] = useState([]);
  const [family, setFamily] = useState<{ id: string; name: string } | null>(null);
  const [status, setStatus] = useState<boolean | null>(null);
  const [requiredErrors, setRequiredErrors] = useState<RequiredErrors>(createInitialRequiredErrors());
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const nameRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);
  const partNumberRef = useRef<any>(null);
  const familyRef = useRef<any>(null);
  const statusRef = useRef<any>(null);

  const statusOptions = [
    { label: translateForm("active"), value: true },
    { label: translateForm("inactive"), value: false },
  ];

  useEffect(() => {
    const fetchFamilies = async (): Promise<void> => {
      if (!plantId) return;
      try {
        const response = await FamilyApi.GetFamiliesByPlant(plantId);
        setFamilies(response.data);
      } catch (error) {
        displayToast(
          "error",
          translateMessages("errorRetrieveData") || translateMessages("error") || "Erro",
          "",
          false,
          3000,
        );
      }
    };
    fetchFamilies();
  }, [plantId, displayToast, translateMessages]);

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedPartNumber = partNumber.trim();

    const nextRequiredErrors: RequiredErrors = {
      family: !family,
      status: status === null,
      partNumber: !trimmedPartNumber,
      name: !trimmedName,
      description: !trimmedDescription,
    };

    if (Object.values(nextRequiredErrors).some(Boolean)) {
      setRequiredErrors(nextRequiredErrors);

      const missingFields: string[] = [];
      if (nextRequiredErrors.family) missingFields.push(translateForm("family") || "Família");
      if (nextRequiredErrors.status) missingFields.push(translateForm("status") || "Status");
      if (nextRequiredErrors.partNumber) missingFields.push(translateForm("partNumber") || "Código");
      if (nextRequiredErrors.name) missingFields.push(translateForm("name") || "Nome");
      if (nextRequiredErrors.description) missingFields.push(translateForm("description") || "Descrição");

      const summary = translateMessages("error") || "Erro";
      const requiredMessage =
        translateMessages("requiredFields") ||
        translateForm("requiredFields") ||
        translateForm("requiredField") ||
        "Campos obrigatórios";

      displayToast(
        "error",
        summary,
        `${requiredMessage}: ${missingFields.join(", ")}`,
        false,
        3000,
      );
      return;
    }

    setRequiredErrors(createInitialRequiredErrors());

    const payload = {
      name: trimmedName,
      description: trimmedDescription,
      partNumber: trimmedPartNumber,
      familyId: family?.id,
      isProducible: status,
    };

    try {
      await ProductApi.CreateProduct(payload);
      displayToast(
        "success",
        translateForm("Success"),
        translateForm("productCreated"),
        false,
        3000,
      );

      setTimeout(() => {
        navigate("/products");
      }, 1000);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.data ||
        err?.data?.data ||
        translateForm("productNotCreated");
      displayToast(
        "error",
        translateMessages("error") || "Erro",
        errorMessage,
        false,
        3000,
      );
    }
  };

  const handleCancel = (): void => setShowConfirmCancel(true);

  const requiredFieldMessage = translateForm("requiredField") || "Campo obrigatório";

  return (
    <FormCard
      title={translateForm("createProduct")}
      icon="inventory_2"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      numColumns={6}
      submitButtonLabel={translateForm("register")}
      cancelButtonLabel={translateForm("Cancel")}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("families")}
          required
          value={family}
          options={families}
          optionLabel="name"
          onChange={(val: any) => {
            setFamily(val);
            setRequiredErrors((prev) => ({ ...prev, family: false }));
            setTimeout(() => statusRef.current?.focus(), 0);
          }}
          placeholder={translateForm("family")}
          disabled={!plantId}
          ref={familyRef}
          className={requiredErrors.family ? "p-invalid" : ""}
        />
        {requiredErrors.family && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("status")}
          required
          value={status}
          options={statusOptions}
          optionLabel="label"
          onChange={(val: any) => {
            setStatus(val);
            setRequiredErrors((prev) => ({ ...prev, status: false }));
            setTimeout(() => nameRef.current?.focus(), 0);
          }}
          placeholder={translateForm("status")}
          ref={statusRef}
          className={requiredErrors.status ? "p-invalid" : ""}
        />
        {requiredErrors.status && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("Name")}
          required
          value={name}
          maxLength={50}
          onChange={(val: any) => {
            setName(val);
            setRequiredErrors((prev) => ({ ...prev, name: false }));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              partNumberRef.current?.focus();
            }
          }}
          placeholder={translateForm("name")}
          ref={nameRef}
          className={requiredErrors.name ? "p-invalid" : ""}
        />
        {requiredErrors.name && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("partNumber")}
          required
          value={partNumber}
          maxLength={15}
          onChange={(val: any) => {
            setPartNumber(val);
            setRequiredErrors((prev) => ({ ...prev, partNumber: false }));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              descriptionRef.current?.focus();
            }
          }}
          placeholder={translateForm("partNumber")}
          ref={partNumberRef}
          className={requiredErrors.partNumber ? "p-invalid" : ""}
        />
        {requiredErrors.partNumber && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="textArea"
          label={translateForm("Description")}
          required
          value={description}
          maxLength={600}
          rows={3}
          onChange={(val: any) => {
            setDescription(val);
            setRequiredErrors((prev) => ({ ...prev, description: false }));
          }}
          placeholder={translateForm("description")}
          ref={descriptionRef}
          className={requiredErrors.description ? "p-invalid" : ""}
        />
        {requiredErrors.description && <small className="p-error">{requiredFieldMessage}</small>}
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/products")}
      />
    </FormCard>
  );
};

export default CreateProductForm;

